import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { UserPlus, ArrowLeft, Clock } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import AnimatedBackground from '../components/AnimatedBackground';
import BackgroundMusic from '../components/BackgroundMusic';
import MathCaptcha from '../components/MathCaptcha';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { toast } = useToast();
  const [pendingApproval, setPendingApproval] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    livingPlace: '',
    houseNumber: '',
    additional: '',
    zipCode: '',
    mobileNumber: '',
    email: '',
    username: '',
    password: '',
    confirmPassword: ''
  });
  const [captchaData, setCaptchaData] = useState({
    captchaId: '',
    captchaAnswer: ''
  });
  const [captchaError, setCaptchaError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleGenderChange = (value) => {
    setFormData({
      ...formData,
      gender: value
    });
  };

  const handleCaptchaChange = (captchaId, captchaAnswer) => {
    setCaptchaData({ captchaId, captchaAnswer });
    setCaptchaError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: 'Passwords do not match',
        description: 'Please make sure your passwords match',
        variant: 'destructive',
      });
      return;
    }

    // Validate CAPTCHA
    if (!captchaData.captchaAnswer) {
      setCaptchaError('Please solve the math problem');
      toast({
        title: 'CAPTCHA Required',
        description: 'Please solve the math problem to continue',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    const { confirmPassword, ...registrationData } = formData;
    const result = await register({
      ...registrationData,
      captchaId: captchaData.captchaId,
      captchaAnswer: parseInt(captchaData.captchaAnswer)
    });

    if (result.success) {
      if (result.pending) {
        setPendingApproval(true);
        toast({
          title: 'Account Created',
          description: 'Your account is pending admin approval.',
        });
      } else {
        toast({
          title: 'Registration Successful',
          description: 'Welcome to FXC Management Services!',
        });
        navigate('/dashboard');
      }
    } else {
      toast({
        title: 'Registration Failed',
        description: result.error,
        variant: 'destructive',
      });
    }

    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0d6571] to-[#0d6571]/80 py-12 px-4">
      <BackgroundMusic audioUrl="/audio/background-music.mp3" />
      <div className="w-full max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="text-white hover:text-white/80 mb-6"
        >
          <ArrowLeft className="mr-2" />
          Back to Home
        </Button>

        {pendingApproval ? (
          <div className="bg-white rounded-2xl shadow-2xl p-12 text-center" data-testid="pending-approval-message">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-amber-100 rounded-full mb-6">
              <Clock className="w-10 h-10 text-amber-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Account Pending Approval</h1>
            <p className="text-gray-600 text-lg mb-6 max-w-md mx-auto">
              Your account has been successfully created and is now awaiting admin approval. You will be able to log in once an administrator approves your access.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8 max-w-md mx-auto">
              <p className="text-amber-800 text-sm font-medium">
                This process usually takes a short time. Please check back later.
              </p>
            </div>
            <Button
              onClick={() => navigate('/login')}
              className="bg-[#0d6571] hover:bg-[#0d6571]/90 text-white rounded-full px-8 py-3 font-semibold"
              data-testid="go-to-login-btn"
            >
              Go to Login
            </Button>
          </div>
        ) : (
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#0d6571] rounded-full mb-4">
              <UserPlus className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Create Your Account</h1>
            <p className="text-gray-600 mt-2">Join FXC Management Services and start your investment journey</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="border-b pb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Personal Information</h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0d6571] focus:ring-0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0d6571] focus:ring-0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Date of Birth <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0d6571] focus:ring-0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Gender <span className="text-red-500">*</span>
                  </label>
                  <Select onValueChange={handleGenderChange} required>
                    <SelectTrigger className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0d6571]">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                      <SelectItem value="prefer-not-to-say">Prefer not to say</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="border-b pb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Address Information</h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Living Place <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    name="livingPlace"
                    value={formData.livingPlace}
                    onChange={handleChange}
                    required
                    placeholder="City"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0d6571] focus:ring-0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    House Number <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    name="houseNumber"
                    value={formData.houseNumber}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0d6571] focus:ring-0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Additional(s)
                  </label>
                  <Input
                    type="text"
                    name="additional"
                    value={formData.additional}
                    onChange={handleChange}
                    placeholder="Apartment, suite, etc. (optional)"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0d6571] focus:ring-0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Zip or Postcode <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0d6571] focus:ring-0"
                  />
                </div>
              </div>
            </div>

            <div className="border-b pb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Mobile Number <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="tel"
                    name="mobileNumber"
                    value={formData.mobileNumber}
                    onChange={handleChange}
                    required
                    placeholder="+31 6 12345678"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0d6571] focus:ring-0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    E-mail Address <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0d6571] focus:ring-0"
                  />
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">Account Information</h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Username <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    required
                    minLength={3}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0d6571] focus:ring-0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={6}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0d6571] focus:ring-0"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Confirm Password <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    minLength={6}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0d6571] focus:ring-0"
                  />
                </div>
              </div>
            </div>

            {/* CAPTCHA Component */}
            <MathCaptcha 
              onCaptchaChange={handleCaptchaChange}
              error={captchaError}
            />

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#0d6571] hover:bg-[#0d6571]/90 text-white rounded-full px-8 py-6 text-lg font-semibold"
            >
              {isSubmitting ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-[#0d6571] hover:text-[#d97841] font-semibold">
                Login here
              </Link>
            </p>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default RegisterPage;
